import { useState, useImperativeHandle, forwardRef } from "react";
import { useDocs } from "../utils/useDocs";
import classes from "./PDFUploader.module.css";
import { useTranslation } from "react-i18next";
import axios from "axios";
import CustomButton from "./CustomButton";

import AllDocumentsSlider from "./AllDocumentsSlider";
import { resolveIcon } from "../utils/resolveIcon";
// import uploadIcon from "../assets/upload_icon.png";
// import addIcon from "../assets/add_icon.png";
// import delete_icon from "../assets/circled_cancel.svg";
import { DoneIcon } from "../assets/registtyIcons/icons";
import { isPathname } from "../utils/isPathName";

const PDFUploader = forwardRef((props, ref) => {
  const { handlePrepareDocs } = useDocs();
  const { t } = useTranslation("general");
  const [docsFields, setDocsFields] = useState({
    count: 1,
    max: 10,
  });

  const handleUploadDocs = async (invoiceId) => {
    const readyToUploadDocs = new FormData();
    readyToUploadDocs.append(
      "imageType",
      `invoice ${props?.invoiceNumber} documents`
    );

    readyToUploadDocs.append("invoiceId", invoiceId);
    readyToUploadDocs.append("invoiceType", props?.invoiceType);
    if (props?.tempDocs.length) {
      props?.tempDocs.forEach((document) => {
        readyToUploadDocs.append(
          isPathname("createNoCustomerInvoice") ? "invoice" : "images",
          document
        );
      });
    } else {
      return;
    }
    const token = localStorage.getItem("token");
    const userId = parseInt(localStorage.getItem("userId"), 10);

    try {
      // If the document is added for the invoice, go to the invoiceFiles routes, else use the normal route

      const url = isPathname("createNoCustomerInvoice")
        ? `${process.env.REACT_APP_URL}/multiContainerInvoiceWithoutC/invoiceMainFiles/${invoiceId}`
        : `${process.env.REACT_APP_URL}/uploadImages`;
      const uploadResponse = await axios.put(url, readyToUploadDocs, {
        headers: {
          Authorization: `Bearer ${token}`,
          "User-Id": userId,
        },
      });

      if (!uploadResponse?.data) {
        console.error("Upload failed");
      } else {
        const updatedDocuments = [
          ...props.invoiceDocs,
          ...uploadResponse?.data?.images,
        ];
        props.setInvoiceDocs(updatedDocuments);
        props?.setTempDocs([]);
        setDocsFields({
          count: 1,
          max: 10,
        });
      }
    } catch (error) {
      console.error("Error uploading documents:", error);
    }
  };

  // function to delete the files preupload
  const handleDeleteFile = (index) => {
    props?.setTempDocs((prev) => {
      const newDocs = [...prev];
      newDocs.splice(index, 1); // Remove the file at the given index
      return newDocs;
    });

    // resetting the input element to empty
    const fileInput = document.getElementById(`invoiceDoc${index + 1}`);
    if (fileInput) {
      fileInput.value = "";
    }
  };

  const generateUploadInputs = () => {
    return Array.from({ length: docsFields.count }).map((_, index) => (
      <div className={classes.inputContainer} key={index}>
        <input
          accept="image/*,application/pdf"
          type="file"
          onChange={(e) => {
            handlePrepareDocs(
              e,
              `Invoice ${props?.invoiceNumber} - image ${index + 1}`
            );
            props?.setTempDocs((prev) => [...prev, e.target.files[0]]);
          }}
          className={`${classes.fileInput}`}
          name="images"
          id={`invoiceDoc${index + 1}`}
          disabled={props?.tempDocs[index]}
        />
        <label htmlFor={`invoiceDoc${index + 1}`}>
          <p className={classes.customFileInput}>
            <img src={resolveIcon("upload")} alt={`Upload image ${index + 1}`} />
            <span>
              {t("uploadDocument")} {index + 1}
            </span>
          </p>
        </label>
        {props?.tempDocs[index] && (
          <div className="o-row o-row o-row--vertical-align_center">
            <span>
              <DoneIcon width={30} height={30} />
            </span>
            <div className={classes.fileName}>
              <span>
                {t("fileName")}: {props?.tempDocs[index]?.name?.slice(0, 30)}
              </span>
              <button
                className={classes.deleteFileButton}
                onClick={() => handleDeleteFile(index)}
              >
                <img src={resolveIcon("deleteCircled")} />
              </button>
            </div>
          </div>
        )}
      </div>
    ));
  };

  useImperativeHandle(ref, () => ({
    handleUploadDocs,
  }));

  const isLightBackground = props.lightBackground ?? true;

  return (
    <>
      <section className={classes.section}>
        <>
          {props?.mode === "upload" ? (
            <div
              className={`${
                classes.pdfUploaderSectionContainer
              } o-column o-column--gap-size_300 ${
                isLightBackground && "natural-gray"
              }`}
            >
              {!props.noTitle && <span>{t("uploadDocs")}: </span>}
              <div
                className={`${classes.pdfUploaderSectionActions} o-column o-column--gap-size_100`}
              >
                {generateUploadInputs()}
                {docsFields.count < docsFields.max && (
                  <div className={`u-padding--top_300`}>
                    <CustomButton
                      title={t("addAnotherDocument")}
                      variant="blank"
                      onClick={() =>
                        setDocsFields((prev) => ({
                          ...prev,
                          count: prev.count < 10 ? ++prev.count : prev.count,
                        }))
                      }
                      icon='addP'
                    />
                  </div>
                )}
              </div>
            </div>
          ) : props?.mode === "show" ? (
            <>
              {props?.documents?.length ? (
                <AllDocumentsSlider
                  documents={props?.documents}
                  setDocuments={props.setInvoiceDocs}
                  title={props?.title || "invoiceAllDocs"}
                  mode={props?.mode}
                />
              ) : null}
            </>
          ) : (
            <>
              <div className={classes.pdfUploaderSection}>
                <AllDocumentsSlider
                  documents={props?.documents}
                  setDocuments={props.setInvoiceDocs}
                  title={props?.title || "invoiceAllDocs"}
                  mode={props?.mode}
                />

                <div className={classes.pdfUploaderSectionActions}>
                  <span>{t("uploadDocs")}: </span>
                  {generateUploadInputs()}
                  {docsFields.count < docsFields.max && (
                    <CustomButton
                      title={t("addAnotherDocument")}
                      variant="blank"
                      onClick={() =>
                        setDocsFields((prev) => ({
                          ...prev,
                          count: prev.count < 10 ? ++prev.count : prev.count,
                        }))
                      }
                      icon='add'
                    />
                  )}
                </div>
              </div>
            </>
          )}
        </>
      </section>
    </>
  );
});

export default PDFUploader;
